import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  BusinessOutlined as SupplierIcon,
  ContactsOutlined as ContactsIcon,
  Inventory2Outlined as MaterialsIcon,
  LocationOnOutlined as LocationIcon,
  MyLocationOutlined as LocateIcon,
  NotesOutlined as NotesIcon,
  SaveOutlined as SaveIcon,
} from '@mui/icons-material';
import { APP_SHAPE } from '../../constants/shape';
import { parseGoogleMapsLink } from '../../services/collection-point.service';
import { listSuppliers } from '../../services/supplier.service';
import { getFullWasteCatalogMaterialOptions, type WasteCatalogOption } from '../../services/waste-catalog.service';
import type {
  CollectionPointStatus,
  Supplier,
  SupplierCollectionPoint,
  SupplierCollectionPointInput,
} from '../../types/supplier.type';
import { WEEKDAYS } from '../../utils/operatingHours';
import { LocationMapPicker } from '../containers/LocationMapPicker';
import DialogHeader from '../common/DialogHeader';

interface CollectionPointFormDialogProps {
  open: boolean;
  organizationId: string;
  /** When set, the form is scoped to this supplier and the supplier picker is hidden. */
  supplierId?: string;
  point?: SupplierCollectionPoint | null;
  saving: boolean;
  submitError?: string | null;
  onClose: () => void;
  onSubmit: (input: SupplierCollectionPointInput) => Promise<boolean>;
}

const fieldSx = { '& .MuiOutlinedInput-root': { borderRadius: `${APP_SHAPE.radiusSm}px` } } as const;
const sectionSx = { p: { xs: 2, sm: 3 }, borderRadius: `${APP_SHAPE.radiusLg}px` } as const;

function SectionTitle({ icon, title, subtitle }: { icon: ReactNode; title: string; subtitle: string }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 2 }}>
      <Box sx={{ color: 'primary.main', display: 'flex', mt: 0.5 }}>{icon}</Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="subtitle1" fontWeight={600}>{title}</Typography>
        <Typography variant="body2" color="text.secondary">{subtitle}</Typography>
      </Box>
    </Box>
  );
}

export default function CollectionPointFormDialog({
  open,
  organizationId,
  supplierId,
  point,
  saving,
  submitError,
  onClose,
  onSubmit,
}: CollectionPointFormDialogProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const [name, setName] = useState('');
  const [status, setStatus] = useState<CollectionPointStatus>('active');
  const [selectedSupplierId, setSelectedSupplierId] = useState('');
  const [supplierOptions, setSupplierOptions] = useState<Supplier[]>([]);
  const [latitude, setLatitude] = useState<number | undefined>(undefined);
  const [longitude, setLongitude] = useState<number | undefined>(undefined);
  const [mapsLink, setMapsLink] = useState('');
  const [mapsLinkLoading, setMapsLinkLoading] = useState(false);
  const [mapsLinkError, setMapsLinkError] = useState<string | null>(null);
  const [address, setAddress] = useState('');
  const [contactName, setContactName] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [materialIds, setMaterialIds] = useState<string[]>([]);
  const [materialOptions, setMaterialOptions] = useState<WasteCatalogOption[]>([]);
  const [operatingDays, setOperatingDays] = useState<string[]>([]);
  const [operatingStart, setOperatingStart] = useState('');
  const [operatingEnd, setOperatingEnd] = useState('');
  const [notes, setNotes] = useState('');
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName(point?.name ?? '');
    setStatus(point?.status === 'archived' ? 'active' : point?.status ?? 'active');
    setSelectedSupplierId(supplierId ?? point?.supplier_id ?? '');
    setLatitude(point?.latitude);
    setLongitude(point?.longitude);
    setMapsLink('');
    setMapsLinkError(null);
    setAddress(point?.address ?? '');
    setContactName(point?.contact_name ?? '');
    setContactPhone(point?.contact_phone ?? '');
    setContactEmail(point?.contact_email ?? '');
    setMaterialIds(point?.material_ids ?? []);
    setOperatingDays(point?.operating_hours?.days ?? []);
    setOperatingStart(point?.operating_hours?.start_time ?? '');
    setOperatingEnd(point?.operating_hours?.end_time ?? '');
    setNotes(point?.notes ?? '');
    setSubmitted(false);

    // Only the waste types this organization handles.
    // Full catalog: a collection point's waste types are the supplier's, not the owner org's basket.
    void getFullWasteCatalogMaterialOptions().then(setMaterialOptions).catch(() => setMaterialOptions([]));
    // Always fetch suppliers: the accepted waste types render from the supplier's set.
    void listSuppliers({ organization_id: organizationId, limit: 500 })
      .then((result) => setSupplierOptions(result.suppliers))
      .catch(() => setSupplierOptions([]));
  }, [open, point, supplierId, organizationId]);

  // The waste types this point accepts, inherited: its own narrowing (legacy or
  // outlet-synced) wins, else the supplier's set -- nobody selects them twice.
  const effectiveSupplierId = supplierId ?? selectedSupplierId;
  const inheritedMaterials = useMemo(() => {
    const supplierMaterialIds = supplierOptions.find((supplier) => supplier.id === effectiveSupplierId)?.material_ids ?? [];
    const ids = materialIds.length ? materialIds : supplierMaterialIds;
    return ids.map((id) => materialOptions.find((option) => option.value === id) ?? { value: id, label: id });
  }, [materialIds, supplierOptions, effectiveSupplierId, materialOptions]);

  const hasLocation = latitude != null && longitude != null && Number.isFinite(latitude) && Number.isFinite(longitude);
  const operatingHoursRangeInvalid = Boolean(operatingStart) && Boolean(operatingEnd) && operatingStart >= operatingEnd;
  const isValid = name.trim().length > 0
    && (supplierId || selectedSupplierId)
    && hasLocation
    && contactName.trim().length > 0
    && (contactPhone.trim() || contactEmail.trim())
    && !operatingHoursRangeInvalid;

  const handleLocate = useCallback(async () => {
    if (!mapsLink.trim()) return;
    setMapsLinkLoading(true);
    setMapsLinkError(null);
    try {
      const result = await parseGoogleMapsLink(organizationId, mapsLink.trim());
      setLatitude(result.latitude);
      setLongitude(result.longitude);
    } catch {
      setMapsLinkError(t('suppliers.collectionPoints.form.mapsLinkError'));
    } finally {
      setMapsLinkLoading(false);
    }
  }, [mapsLink, organizationId, t]);

  const handleSubmit = async () => {
    setSubmitted(true);
    if (!isValid || latitude == null || longitude == null) return;
    const success = await onSubmit({
      supplier_id: supplierId ?? selectedSupplierId,
      name: name.trim(),
      address: address.trim() || null,
      latitude,
      longitude,
      google_maps_url: mapsLink.trim() || point?.google_maps_url || null,
      status,
      contact_name: contactName.trim(),
      contact_phone: contactPhone.trim() || undefined,
      contact_email: contactEmail.trim() || undefined,
      material_ids: point?.material_ids ?? [],
      operating_hours: operatingDays.length || operatingStart || operatingEnd
        ? { days: operatingDays, start_time: operatingStart || null, end_time: operatingEnd || null }
        : null,
      notes: notes.trim() || null,
    });
    if (success) onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      fullWidth
      fullScreen={fullScreen}
      maxWidth="md"
      scroll="paper"
      PaperProps={{ sx: { maxHeight: { sm: '92vh' }, borderRadius: { sm: `${APP_SHAPE.radiusXl}px` } } }}
    >
      <DialogHeader
        title={point ? t('suppliers.collectionPoints.form.editTitle') : t('suppliers.collectionPoints.form.addTitle')}
        subtitle={point ? t('suppliers.collectionPoints.form.editSubtitle') : t('suppliers.collectionPoints.form.addSubtitle')}
      />
      <DialogContent sx={{ p: { xs: 2, sm: 3 }, bgcolor: 'background.default' }}>
        <Stack spacing={3}>
          {submitError && <Alert severity="error">{submitError}</Alert>}

          <Paper variant="outlined" sx={sectionSx}>
            <SectionTitle
              icon={<SupplierIcon fontSize="small" />}
              title={t('suppliers.collectionPoints.form.detailsSection.title')}
              subtitle={t('suppliers.collectionPoints.form.detailsSection.subtitle')}
            />
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: supplierId ? '2fr 1fr' : '1fr 1fr 1fr' }, gap: 3 }}>
              <TextField
                label={t('suppliers.collectionPoints.form.name')}
                value={name}
                onChange={(event) => setName(event.target.value)}
                required
                error={submitted && !name.trim()}
                helperText={submitted && !name.trim() ? t('suppliers.collectionPoints.form.nameError') : ' '}
                sx={fieldSx}
              />
              {!supplierId && (
                <Autocomplete
                  options={supplierOptions}
                  value={supplierOptions.find((option) => option.id === selectedSupplierId) ?? null}
                  onChange={(_, value) => setSelectedSupplierId(value?.id ?? '')}
                  getOptionLabel={(option) => option.name}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('suppliers.collectionPoints.form.supplier')}
                      required
                      error={submitted && !selectedSupplierId}
                      helperText={submitted && !selectedSupplierId ? t('suppliers.collectionPoints.form.supplierError') : ' '}
                      sx={fieldSx}
                    />
                  )}
                />
              )}
              <FormControl sx={fieldSx}>
                <InputLabel id="collection-point-status-label">{t('suppliers.collectionPoints.form.status')}</InputLabel>
                <Select
                  labelId="collection-point-status-label"
                  label={t('suppliers.collectionPoints.form.status')}
                  value={status}
                  onChange={(event) => setStatus(event.target.value as CollectionPointStatus)}
                >
                  <MenuItem value="active">{t('suppliers.collectionPoints.form.active')}</MenuItem>
                  <MenuItem value="archived">{t('suppliers.collectionPoints.form.archived')}</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Paper>

          <Paper variant="outlined" sx={sectionSx}>
            <SectionTitle
              icon={<LocationIcon fontSize="small" />}
              title={t('suppliers.collectionPoints.form.locationSection.title')}
              subtitle={t('suppliers.collectionPoints.form.locationSection.subtitle')}
            />
            <Stack spacing={2}>
              <Box sx={{ display: 'flex', gap: 1.5, flexDirection: { xs: 'column', sm: 'row' } }}>
                <TextField
                  fullWidth
                  label={t('suppliers.collectionPoints.form.mapsLink')}
                  placeholder={t('suppliers.collectionPoints.form.mapsLinkPlaceholder')}
                  value={mapsLink}
                  onChange={(event) => { setMapsLink(event.target.value); setMapsLinkError(null); }}
                  error={Boolean(mapsLinkError)}
                  helperText={mapsLinkError || ' '}
                  sx={fieldSx}
                />
                <Button
                  variant="outlined"
                  onClick={() => void handleLocate()}
                  disabled={!mapsLink.trim() || mapsLinkLoading}
                  startIcon={mapsLinkLoading ? <CircularProgress size={16} /> : <LocateIcon />}
                  sx={{ flexShrink: 0, height: 56 }}
                >
                  {t('suppliers.collectionPoints.form.locate')}
                </Button>
              </Box>
              <LocationMapPicker
                latitude={latitude}
                longitude={longitude}
                onLocationChange={(lat, lng) => { setLatitude(lat); setLongitude(lng); }}
                height={260}
              />
              {submitted && !hasLocation && (
                <Alert severity="error">{t('suppliers.collectionPoints.form.latitudeError')}</Alert>
              )}
              <TextField
                label={t('suppliers.collectionPoints.form.address')}
                value={address}
                onChange={(event) => setAddress(event.target.value)}
                sx={fieldSx}
              />
            </Stack>
          </Paper>

          <Paper variant="outlined" sx={sectionSx}>
            <SectionTitle
              icon={<ContactsIcon fontSize="small" />}
              title={t('suppliers.collectionPoints.form.contactSection.title')}
              subtitle={t('suppliers.collectionPoints.form.contactSection.subtitle')}
            />
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 3 }}>
              <TextField
                label={t('suppliers.collectionPoints.form.contactName')}
                value={contactName}
                onChange={(event) => setContactName(event.target.value)}
                required
                error={submitted && !contactName.trim()}
                helperText={submitted && !contactName.trim() ? t('suppliers.collectionPoints.form.contactNameError') : ' '}
                sx={fieldSx}
              />
              <TextField
                label={t('suppliers.collectionPoints.form.phone')}
                value={contactPhone}
                onChange={(event) => setContactPhone(event.target.value)}
                error={submitted && !contactPhone.trim() && !contactEmail.trim()}
                helperText={submitted && !contactPhone.trim() && !contactEmail.trim() ? t('suppliers.collectionPoints.form.contactMethodError') : ' '}
                sx={fieldSx}
              />
              <TextField
                label={t('suppliers.collectionPoints.form.email')}
                type="email"
                value={contactEmail}
                onChange={(event) => setContactEmail(event.target.value)}
                error={submitted && !contactPhone.trim() && !contactEmail.trim()}
                helperText=" "
                sx={fieldSx}
              />
            </Box>
          </Paper>

          <Paper variant="outlined" sx={sectionSx}>
            <SectionTitle
              icon={<MaterialsIcon fontSize="small" />}
              title={t('suppliers.collectionPoints.form.materialsSection.title')}
              subtitle={t('suppliers.collectionPoints.form.materialsSection.subtitle')}
            />
            {inheritedMaterials.length > 0 ? (
              <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                {inheritedMaterials.map((material) => (
                  <Chip key={material.value} size="small" variant="outlined" label={material.label} />
                ))}
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary">
                {t('suppliers.collectionPoints.form.materialsSection.emptyInherited')}
              </Typography>
            )}
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>
                {t('suppliers.collectionPoints.form.operatingHours')}
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 1fr 1fr' }, gap: 2 }}>
                <FormControl sx={fieldSx}>
                  <InputLabel id="collection-point-operating-days-label">
                    {t('suppliers.collectionPoints.form.operatingDays')}
                  </InputLabel>
                  <Select
                    multiple
                    labelId="collection-point-operating-days-label"
                    label={t('suppliers.collectionPoints.form.operatingDays')}
                    value={operatingDays}
                    onChange={(event) => setOperatingDays(event.target.value as string[])}
                    renderValue={(selected) => (selected as string[]).map((day) => t(`collectionSites.days.${day}`)).join(', ')}
                  >
                    {WEEKDAYS.map((day) => (
                      <MenuItem key={day} value={day}>{t(`collectionSites.days.${day}`)}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  label={t('suppliers.collectionPoints.form.startTime')}
                  type="time"
                  value={operatingStart}
                  onChange={(event) => setOperatingStart(event.target.value)}
                  error={operatingHoursRangeInvalid}
                  InputLabelProps={{ shrink: true }}
                  sx={fieldSx}
                />
                <TextField
                  label={t('suppliers.collectionPoints.form.endTime')}
                  type="time"
                  value={operatingEnd}
                  onChange={(event) => setOperatingEnd(event.target.value)}
                  error={operatingHoursRangeInvalid}
                  helperText={operatingHoursRangeInvalid ? t('suppliers.collectionPoints.form.operatingHoursRangeError') : ' '}
                  InputLabelProps={{ shrink: true }}
                  sx={fieldSx}
                />
              </Box>
            </Box>
          </Paper>

          <Paper variant="outlined" sx={sectionSx}>
            <SectionTitle
              icon={<NotesIcon fontSize="small" />}
              title={t('suppliers.collectionPoints.form.notesSection.title')}
              subtitle={t('suppliers.collectionPoints.form.notesSection.subtitle')}
            />
            <TextField
              fullWidth
              label={t('suppliers.collectionPoints.form.notes')}
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
              multiline
              minRows={3}
              sx={fieldSx}
            />
          </Paper>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: { xs: 2, sm: 3 }, py: 2, borderTop: 1, borderColor: 'divider' }}>
        <Button onClick={onClose} disabled={saving}>{t('suppliers.collectionPoints.form.cancel')}</Button>
        <Button
          variant="contained"
          startIcon={saving ? <CircularProgress color="inherit" size={16} /> : <SaveIcon />}
          onClick={() => void handleSubmit()}
          disabled={saving}
        >
          {saving ? t('suppliers.collectionPoints.form.saving') : point ? t('suppliers.collectionPoints.form.save') : t('suppliers.collectionPoints.form.create')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
